import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { messageFor } from '@/api/errors';
import { useSwitchTenant } from '@/api/queries/auth';
import { useOrganisation } from '@/api/queries/organisation';
import { roleLabel } from '@/auth/permissions';
import { useSession } from '@/auth/use-session';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** The organisation's logo, or a building icon. */
function OrganisationMark({ logo }: { logo: string | null | undefined }) {
  return logo ? (
    <img src={logo} alt="" className="size-5 shrink-0 rounded-sm object-contain" />
  ) : (
    <Building2 aria-hidden="true" className="text-muted-foreground" />
  );
}

/**
 * Shows the active organisation with its logo (Step 17); a menu appears only for people in
 * several organisations.
 */
export function TenantSwitcher() {
  const { tenant, tenants } = useSession();
  const org = useOrganisation(Boolean(tenant)).data;
  const switchTenant = useSwitchTenant();
  const navigate = useNavigate();
  const name = org?.name ?? tenant?.name ?? 'No organisation';
  if (!tenant) return null;

  // Only people in several organisations need to switch.
  if (tenants.length <= 1)
    return (
      <span className="hidden max-w-56 items-center gap-2 px-2 text-sm font-medium sm:flex">
        <OrganisationMark logo={org?.logo} />
        <span className="truncate">{name}</span>
      </span>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="max-w-56 gap-2 px-2"
          aria-label={`Organisation: ${name}. Switch organisation`}
        >
          <OrganisationMark logo={org?.logo} />
          <span className="truncate">{name}</span>
          <ChevronsUpDown aria-hidden="true" className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Your organisations
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tenants.map((t) => (
          <DropdownMenuItem
            key={t.id}
            disabled={switchTenant.isPending || t.status !== 'active'}
            onSelect={() => {
              if (t.id === tenant?.id) return;
              switchTenant.mutate(t.id, {
                onSuccess: (result) => {
                  toast.success(`Switched to ${result.tenant.name}`);
                  void navigate('/home');
                },
                onError: (err) => toast.error(messageFor(err)),
              });
            }}
          >
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate">{t.name}</span>
              <span className="text-xs text-muted-foreground">
                {roleLabel(t.role)}
                {t.status !== 'active' && ' · suspended'}
              </span>
            </span>
            {t.id === tenant?.id && <Check aria-hidden="true" className="text-brand-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
